import React from 'react';
import PropTypes from 'prop-types';
import { isBDD } from '../../../utils/bdd';
import { listLoopPatternWithBypassedIntro } from '../utils';

/** Entry (decision) page path. */
export const STR_QUESTION_PATH =
  'supporting-evidence/service-treatment-records';

/** arrayPath for the STR upload loop. */
export const STR_UPLOAD_ARRAY_PATH = 'serviceTreatmentRecordsUploads';

/** Item page path (with :index) — used to deep-link into the loop. */
export const STR_UPLOAD_ITEM_PATH =
  'supporting-evidence/service-treatment-records-upload/:index/document';

/** Loop summary (review) page path. */
export const STR_UPLOAD_SUMMARY_PATH =
  'supporting-evidence/service-treatment-records-summary';

/** Entry page answer: true = "Yes, I'll submit them now". */
export const STR_ANSWER_KEY = 'view:hasServiceTreatmentRecords';

/** Record types. Codes match the live 526 STR upload (L451, L450). */
export const STR_ATTACHMENT_LABELS = {
  L451: 'Medical',
  L450: 'Dental',
};

export const STR_HINT_TEXT =
  'You can upload a .pdf, .jpg, .jpeg, .png, .gif, .bmp, or .txt file. Each file should be no larger than 100 MB. Larger files may take longer to upload, depending on the internet connection.';

/** True when the BDD claimant chose to upload their STR now. */
export const isUploadingStr = formData =>
  isBDD(formData) && formData?.[STR_ANSWER_KEY] === true;

/**
 * Loop descriptor for the skip-intro navigation helpers: entering the loop
 * goes straight to the first item page (add mode) or the loop summary.
 */
export const STR_UPLOAD_LOOP = {
  arrayPath: STR_UPLOAD_ARRAY_PATH,
  itemPath: STR_UPLOAD_ITEM_PATH,
  summaryPath: STR_UPLOAD_SUMMARY_PATH,
};

export const strUploadBypassedIntro = listLoopPatternWithBypassedIntro(
  STR_UPLOAD_LOOP,
  STR_QUESTION_PATH,
);

/**
 * Delete modal body, matching the file input's own delete modal on the upload
 * page ("We'll delete the uploaded file" with the file name in bold below).
 */
const DeleteFileDescription = props => (
  <>
    We’ll delete the uploaded file{' '}
    <strong className="vads-u-display--block">
      {props.getItemName(props.itemData, props.index, props.formData)}
    </strong>
  </>
);

DeleteFileDescription.propTypes = {
  formData: PropTypes.object,
  getItemName: PropTypes.func,
  index: PropTypes.number,
  itemData: PropTypes.object,
};

/** @type {ArrayBuilderOptions} */
export const serviceTreatmentRecordsOptions = {
  arrayPath: STR_UPLOAD_ARRAY_PATH,
  nounSingular: 'service treatment record',
  nounPlural: 'service treatment records',
  required: isUploadingStr,
  isItemIncomplete: item =>
    !item?.uploadedDocument?.confirmationCode || !item?.attachmentId,
  text: {
    getItemName: item =>
      item?.uploadedDocument?.name || 'Service treatment record',
    cardDescription: item => STR_ATTACHMENT_LABELS[item?.attachmentId] || '',
    cancelAddButtonText: () => 'Cancel adding this file',
    cancelAddYes: 'Yes, cancel adding this file',
    cancelAddNo: 'No, continue adding this file',
    // Delete modal (review page and Step 4 summary) matches the file input's
    // own delete modal on the upload page.
    deleteTitle: 'Delete this file?',
    deleteDescription: DeleteFileDescription,
    // The loop is required while "Yes" is selected, but the Veteran can still
    // change their answer on the entry page, so deleting the last file uses
    // the same message instead of the platform's "you'll need at least one".
    deleteNeedAtLeastOneDescription: DeleteFileDescription,
    deleteYes: 'Yes, delete this',
    deleteNo: 'No, keep this',
  },
};
